import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import type { ErrorEvent, TransactionEvent, Breadcrumb } from '@sentry/core'
import './index.css'
import App from './App'
import { migrateLegacyTokens } from '@/utils/portalSlug'

// Sanitize URLs to remove sensitive tokens before sending to Sentry
function sanitizeUrl(url: string): string {
  // Redact token/session query parameters
  // Match at start-of-string as well as after `?`/`&`: Sentry often provides
  // `event.request.query_string` without a leading `?`, so a token in the first
  // parameter position (e.g. `token=...&x=y`) would otherwise slip through.
  let sanitized = url.replace(
    /(^|[?&])(token|session|reset_token)=[^&]*/gi,
    '$1$2=[REDACTED]',
  )
  // Redact token path segment in /intake/:token routes (not /internal/intake/:id)
  sanitized = sanitized.replace(
    /(?<!\/internal)(\/intake\/)[^/?#]+/i,
    '$1[REDACTED]',
  )
  return sanitized
}

// Field lists where token-bearing URLs appear, per Sentry surface. They differ
// intentionally: replay's internal click frames add `route`; performance-span
// `data` uses dotted keys. Centralizing them keeps the scrubbing logic in one
// place so a new field is added once rather than drifting across call sites.
const BREADCRUMB_URL_FIELDS = ['url', 'to', 'from'] as const
const REPLAY_BREADCRUMB_URL_FIELDS = ['url', 'route', 'to', 'from'] as const
const SPAN_DATA_URL_FIELDS = [
  'browser.script.invoker',
  'code.filepath',
  'http.url',
  'url',
] as const

// Scrub sanitizeUrl over the named string fields of a record, in place.
function sanitizeUrlFields(
  data: Record<string, unknown> | undefined | null,
  fields: readonly string[],
): void {
  if (!data) return
  for (const field of fields) {
    if (typeof data[field] === 'string') {
      data[field] = sanitizeUrl(data[field] as string)
    }
  }
}

function sanitizeBreadcrumbs(breadcrumbs: Breadcrumb[]): Breadcrumb[] {
  return breadcrumbs.map((breadcrumb) => {
    const bc = { ...breadcrumb }
    if (bc.message) bc.message = sanitizeUrl(bc.message)
    if (bc.data) {
      bc.data = { ...bc.data }
      sanitizeUrlFields(bc.data, BREADCRUMB_URL_FIELDS)
    }
    return bc
  })
}

// Initialize Sentry for staging and production environments only
const environment = import.meta.env.VITE_ENVIRONMENT || 'development'
const sentryDsn = import.meta.env.VITE_SENTRY_DSN

if (environment !== 'development' && sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        beforeAddRecordingEvent(event) {
          // Scrub tokens from URLs captured in replay recording frames.
          // Recording custom events use tag 'breadcrumb' | 'performanceSpan' |
          // 'options' (there is no 'navigation' tag), so URLs must be scrubbed
          // from the span description and navigation breadcrumb data instead.
          const data = event.data as unknown as
            | {
                tag?: string
                payload?: {
                  description?: unknown
                  category?: unknown
                  data?: {
                    to?: unknown
                    from?: unknown
                    previous?: unknown
                    url?: unknown
                    route?: unknown
                  }
                }
              }
            | undefined
          const payload = data?.payload
          if (!payload) return event

          // Navigation / SPA history / fetch / xhr / resource spans: the URL
          // lives in payload.description (and the prior URL in data.previous).
          if (data.tag === 'performanceSpan') {
            if (typeof payload.description === 'string') {
              payload.description = sanitizeUrl(payload.description)
            }
            sanitizeUrlFields(payload.data, ['previous'])
          }

          // Internal replay breadcrumb frames carry URLs in different data
          // fields by category: `to`/`from` (navigation), `url`/`route`
          // (ui.slowClickDetected, ui.multiClick) and `url` (replay.hydrate-error)
          // — all captured raw from window.location. Standard breadcrumbs are
          // already scrubbed by beforeBreadcrumb before they reach replay, but
          // these internal frames bypass it, so scrub every known field here.
          if (data.tag === 'breadcrumb') {
            sanitizeUrlFields(payload.data, REPLAY_BREADCRUMB_URL_FIELDS)
          }

          return event
        },
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: environment === 'staging' ? 1.0 : 0.1,
    // Session Replay
    replaysSessionSampleRate: environment === 'staging' ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Scrub sensitive tokens from error events
    beforeSend(event: ErrorEvent) {
      // The error's transaction is the active route name, which for browser
      // tracing is the raw pathname (e.g. /intake/:token).
      if (event.transaction) event.transaction = sanitizeUrl(event.transaction)
      // The message and exception values can embed a URL (e.g. a thrown Error
      // referencing window.location.href).
      if (typeof event.message === 'string') {
        event.message = sanitizeUrl(event.message)
      }
      if (event.exception?.values) {
        for (const exception of event.exception.values) {
          if (typeof exception.value === 'string') {
            exception.value = sanitizeUrl(exception.value)
          }
        }
      }
      // The root span's URL data lives in contexts.trace, not event.spans.
      sanitizeUrlFields(event.contexts?.trace?.data, SPAN_DATA_URL_FIELDS)
      if (event.request) {
        if (event.request.url) event.request.url = sanitizeUrl(event.request.url)
        if (typeof event.request.query_string === 'string') {
          event.request.query_string = sanitizeUrl(event.request.query_string)
        }
        if (event.request.headers) {
          const headers = { ...event.request.headers }
          if (headers['Authorization']) headers['Authorization'] = '[REDACTED]'
          if (headers['authorization']) headers['authorization'] = '[REDACTED]'
          event.request.headers = headers
        }
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = sanitizeBreadcrumbs(event.breadcrumbs)
      }
      return event
    },

    // Scrub sensitive tokens from transaction/performance events
    beforeSendTransaction(event: TransactionEvent) {
      if (event.transaction) event.transaction = sanitizeUrl(event.transaction)
      if (event.request) {
        if (event.request.url) event.request.url = sanitizeUrl(event.request.url)
        if (typeof event.request.query_string === 'string') {
          event.request.query_string = sanitizeUrl(event.request.query_string)
        }
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = sanitizeBreadcrumbs(event.breadcrumbs)
      }
      // Scrub tokens from span descriptions and data (caught by
      // browserTracingIntegration), including the root span in contexts.trace.
      sanitizeUrlFields(event.contexts?.trace?.data, SPAN_DATA_URL_FIELDS)
      if (event.spans) {
        for (const span of event.spans) {
          if (typeof span.description === 'string') {
            span.description = sanitizeUrl(span.description)
          }
          sanitizeUrlFields(span.data, SPAN_DATA_URL_FIELDS)
        }
      }
      return event
    },

    // Scrub sensitive tokens from breadcrumbs as they are created
    beforeBreadcrumb(breadcrumb: Breadcrumb) {
      if (breadcrumb.message) breadcrumb.message = sanitizeUrl(breadcrumb.message)
      sanitizeUrlFields(breadcrumb.data, BREADCRUMB_URL_FIELDS)
      return breadcrumb
    },
  })

  // Session-replay events bypass beforeSend/beforeSendTransaction but DO run
  // through global event processors. They carry a top-level `urls` array
  // captured raw from window.location (path + query), so token-bearing URLs
  // must be scrubbed here as well. The initial URL is the first entry.
  Sentry.addEventProcessor((event) => {
    if (event.type === 'replay_event') {
      const replayEvent = event as typeof event & { urls?: unknown }
      if (Array.isArray(replayEvent.urls)) {
        replayEvent.urls = replayEvent.urls.map((url) =>
          typeof url === 'string' ? sanitizeUrl(url) : url,
        )
      }
    }
    return event
  })
}

// Migrate pre-namespacing auth tokens into the per-slug namespace (now stored
// per portal slug) so already-logged-in users are not forced to re-login on
// deploy. Runs before React mounts — and before any axios interceptor or auth
// context reads a token — so the migrated token is in place for session restore.
// Guarded so a throwing localStorage (Safari Lockdown/Private, storage disabled
// by policy, partitioned/embedded storage) can never block first paint: the app
// still mounts and degrades to "no stored session → login" instead of a blank
// page. The storage calls inside are already individually safe (see safeStorage);
// this is a belt-and-suspenders backstop for anything added to the boot path.
try {
  migrateLegacyTokens()
} catch {
  // Never let token migration block React from mounting.
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
)
